import { cn } from 'lib/utils'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from 'primitives/base/select'
import { Input } from 'primitives/input'
import { Label } from 'primitives/label'
import { type InputHTMLAttributes, type ReactNode, useId } from 'react'

type DropdownOption = string | { value: string; label: string }

interface DropdownInputProps {
  /** Dropdown choices. Accepts a list of strings (value === label) or {value, label} objects. */
  options: DropdownOption[]
  /** Currently-selected dropdown value. */
  selectedOption?: string
  /** Fires when the user picks a different dropdown option. */
  onOptionChange?: (value: string) => void
  /** Current text input value. */
  value?: string
  /** Fires when the user types in the text input. */
  onValueChange?: (value: string) => void
  placeholder?: string
  /** Width of the dropdown trigger. Override per consumer (default 100px). */
  selectTriggerClassName?: string
  /** Override the outer wrapper class — e.g. to set the overall width. */
  containerClassName?: string
  /** Optional label rendered above the control. */
  label?: string
  /** id for the text input (used by the optional label too). Auto-generated when omitted. */
  id?: string
  /** Optional icon rendered inside the text input on the right. */
  endIcon?: ReactNode
  selectAriaLabel?: string
  inputAriaLabel?: string
  selectTestId?: string
  inputTestId?: string
  disabled?: boolean
  inputMode?: InputHTMLAttributes<HTMLInputElement>['inputMode']
}

const normalize = (o: DropdownOption) => (typeof o === 'string' ? { value: o, label: o } : o)

/**
 * Connected dropdown + text input with a single shared border. Use when the
 * dropdown choice disambiguates what the text input means — e.g. HTTP
 * method + URL, or "Brand vs Campaign" + search term.
 *
 * Visuals mirror the HTTP-node ApiUrlInput so the pattern stays consistent
 * across the app.
 */
export const DropdownInput = ({
  options,
  selectedOption,
  onOptionChange,
  value,
  onValueChange,
  placeholder,
  selectTriggerClassName = 'w-[100px]',
  containerClassName = 'w-full',
  label,
  id,
  endIcon,
  selectAriaLabel,
  inputAriaLabel,
  selectTestId,
  inputTestId,
  disabled,
  inputMode,
}: DropdownInputProps) => {
  const normalized = options.map(normalize)
  // Ensure the optional Label always has a valid htmlFor target even when
  // consumers don't pass an explicit id.
  const reactId = useId()
  const inputId = id ?? reactId

  return (
    <div className="grid items-center gap-1.5">
      {label && <Label htmlFor={inputId}>{label}</Label>}
      <div
        className={cn(
          'flex overflow-hidden rounded-md border border-input ring-offset-background focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2',
          containerClassName,
        )}
      >
        <Select value={selectedOption} onValueChange={onOptionChange} disabled={disabled}>
          <SelectTrigger
            className={cn(
              'border-none rounded-none focus:ring-0 bg-background',
              inputMode === 'url' && 'font-jetbrains tracking-normal',
              selectTriggerClassName,
            )}
            aria-label={selectAriaLabel}
            data-testid={selectTestId}
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {normalized.map((o) => (
              <SelectItem
                key={o.value}
                value={o.value}
                className={cn(inputMode === 'url' && 'font-jetbrains tracking-normal')}
              >
                {o.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <div className="w-px bg-input" />
        <Input
          id={inputId}
          type="text"
          inputMode={inputMode}
          value={value}
          onChange={(e) => onValueChange?.(e.target.value)}
          placeholder={placeholder}
          aria-label={inputAriaLabel}
          data-testid={inputTestId}
          disabled={disabled}
          endIcon={endIcon}
          className="flex-grow border-none focus-visible:ring-0 rounded-none"
        />
      </div>
    </div>
  )
}
